import { Component, OnDestroy, OnInit } from '@angular/core';
import { Logger } from 'common/utility/logger/logger';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, filter, Subject, take, takeUntil } from 'rxjs';
import { EdgeDevice, EdgeDeviceService } from 'services/edge-device/edge-device.service';
import { OrgDeviceSelection } from 'components/org-device-assignment/org-device-assignment.component';
import { NotificationService } from 'services/notification/notification.service';
import { SpinnerTypes } from 'services/loading/spinner/spinner-type';
import { BatteryStringsService } from 'src/app/services/battery-strings/battery-strings.service';
import { EdgeDeviceDataSource } from './EdgeDeviceDatasource';

const languageSection: string = 'component.select.edge-device'

const ColumnName = {
  EdgeId: "EdgeId",
  Label: "Label"
}

const inputDebounceMilliseconds: number = 600
const reloadDataDelay: number = 3000

@Component({
  selector: 'ems-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit, OnDestroy {


  private unsubscribe: Subject<void> = new Subject()
  private dataSource: EdgeDeviceDataSource
  private rowMonitor = new Map<string, Subject<EdgeDevice>>();
  private dataUpdated: Subject<string> = new Subject()



  public selectedEdgeDevice: any = {}
  public deviceRowClicked: boolean = false

  public get DataSource(): EdgeDeviceDataSource {
    return this.dataSource
  }

  public get EdgeDeviceTitle(): string {
    return `${languageSection}.organization-title`
  }

  public tableHeader(key: string): string {
    return `${languageSection}.table-headers.${key}`
  }

  public get ColumnName(): typeof ColumnName {
    return ColumnName
  }

  public placeholder(key: string): string {
    return `${languageSection}.placeholders.${key}`
  }

  public RowSelected(edgeDevice: any): boolean {
    return !!this.selectedEdgeDevice && this.selectedEdgeDevice.Id === edgeDevice?.Id
  }

  public get Columns(): Array<string> {
    return [ColumnName.EdgeId, ColumnName.Label];
  }

  public get spinnerName(): string {
    return "c6946941744344cca07bb113eea4e501";
  }

  public get spinnerType(): string {
    return SpinnerTypes.BallClipRotate;
  }


  public onRowClicked(edgeDevice: any) {
    Logger.debug(`Row clicked: ${edgeDevice}`);
    this.selectedEdgeDevice = edgeDevice;
    this.deviceRowClicked = true;
    this.batteryStringsService.getBatteries(edgeDevice.edgeId).pipe(
      take(1),
      takeUntil(this.unsubscribe),
      catchError(err => {
        Logger.error(`Failed to get battery strings for ${edgeDevice.edgeId}: ${err}`);
        return EMPTY;
      })
    ).subscribe((result: any) => {
      Logger.debug(`Battery strings result: ${JSON.stringify(result)}`);
    });
  }

  public onOrgDeviceSelectionChange(selection: OrgDeviceSelection): void {
    Logger.debug(`Org device selection changed: org=${selection.organization.id}, devices=${selection.deviceIds}`)
  }

  public onLabelChange(label: string, id: string) {
    this.getRowSubject(id).next(
      {
        edgeId: id,
        label: label
      });
  }

  constructor(
    private edgeDeviceService: EdgeDeviceService,
    private batteryStringsService: BatteryStringsService,
    private notificationService: NotificationService,
    private spinner: NgxSpinnerService,
  ) {
    this.dataSource = new EdgeDeviceDataSource(this.edgeDeviceService)
    this.dataSource.loadEdgeDevices()
    this.monitorDataRefresh()
    this.selectFirstEdgeDevice()
  }

  private selectFirstEdgeDevice(): void {
    this.dataSource.EdgeDevices.pipe(
      filter((devices: EdgeDevice[]) => !!devices?.length),
      take(1),
      takeUntil(this.unsubscribe)
    ).subscribe((devices: EdgeDevice[]) => {
      this.selectedEdgeDevice = devices[0]
    })
  }

  ngOnDestroy(): void {
    // this.stopSpinner()
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  ngOnInit(): void {
    this.loadingSpinner()
  }

  private getRowSubject(rowId: string): Subject<EdgeDevice> {
    if (!this.rowMonitor.has(rowId)) {
      const subject = new Subject<EdgeDevice>();

      subject.pipe(
        debounceTime(inputDebounceMilliseconds),
        distinctUntilChanged((prev, curr) => {
          return prev.label === curr.label
        }),
        takeUntil(this.unsubscribe)
      ).subscribe(value => {
        Logger.debug(`Saving Data: id '${value.edgeId}' | label '${value.label}'`);
        this.edgeDeviceService.update(value.edgeId, value.label).pipe(
          take(1),
          catchError(err => {
            Logger.error(`Failed to save user: ${value.edgeId}, ${err}`);
            this.notificationService.notify({
              message: `Label must be unique`,
              notificationType: 'error'
            })
            return EMPTY;
          })
        ).subscribe(result => {
          
          Logger.debug(`save result, ${result}`);
          this.dataUpdated.next(value.edgeId)
        })
      });

      this.rowMonitor.set(rowId, subject);
    }

    return this.rowMonitor.get(rowId)!;
  }

  private monitorDataRefresh(): void {
    this.dataUpdated.pipe(
      debounceTime(1000),
    ).subscribe((id) => {
      this.edgeDeviceService.refreshEdgeDevices()
      this.notificationService.notify({ message: `${id} Saved` })
    })
  }


  private loadingSpinner(): void {
    this.dataSource.Loading.pipe(
      takeUntil(this.unsubscribe)
    ).subscribe(loading => {
      loading ? this.startSpinner() : this.stopSpinner()
    })
  }

  private startSpinner(): void {
    this.spinner.show(this.spinnerName)
  }

  private stopSpinner(): void {
    this.spinner.hide(this.spinnerName);
  }

}

